import {OptimizedLookupData} from '../types/lookup-data.types';

/**
 * As-of-week resolution for effective-dated config version series.
 * Mirrors Certavue/CalculationEngine/ConfigResolver.cs
 * PLEASE KEEP IN SYNC.
 *
 * A version is in force from its effectiveFromWeek (global week number) until
 * superseded by the next version. Resolution picks the version with the
 * greatest effectiveFromWeek <= weekNumber; weeks before the earliest version
 * use the earliest version; an empty/absent series falls back to the flat
 * current configuration.
 */

// Canonical "since inception" week used by rewrite-history writes.
export const INCEPTION_WEEK = 1;

export function resolveAsOf<T extends {effectiveFromWeek: number}>(
    versions: T[] | undefined,
    weekNumber: number
): T | undefined {
    if (!versions || versions.length === 0) return undefined;

    let best: T | undefined;
    let earliest: T | undefined;
    for (const version of versions) {
        if (!earliest || version.effectiveFromWeek < earliest.effectiveFromWeek) {
            earliest = version;
        }
        if (
            version.effectiveFromWeek <= weekNumber &&
            (!best || version.effectiveFromWeek > best.effectiveFromWeek)
        ) {
            best = version;
        }
    }

    return best ?? earliest;
}

// Perf-only caches (TS side; no C# equivalent needed): the engine resolves
// config per resource and per assignment cell, so resolution is memoized per
// (lookup, week). resolveAsOf itself is unchanged — results are identical to
// an uncached call, so ConfigResolver.cs parity is unaffected.
type WorkingHoursConfig = {baseHoursPerWeek: number; defaultHoursPerDay: number};
type ThresholdsConfig = {
    additionalDemandAmberThreshold: number;
    additionalDemandRedThreshold: number;
    baselineAmberThreshold?: number | null;
};
const workingHoursCache = new WeakMap<
    OptimizedLookupData,
    Map<number, WorkingHoursConfig>
>();
const thresholdsCache = new WeakMap<
    OptimizedLookupData,
    Map<number, ThresholdsConfig>
>();

export function resolveWorkingHours(
    lookup: OptimizedLookupData,
    weekNumber: number
): WorkingHoursConfig {
    let byWeek = workingHoursCache.get(lookup);
    if (!byWeek) {
        byWeek = new Map<number, WorkingHoursConfig>();
        workingHoursCache.set(lookup, byWeek);
    }
    const cached = byWeek.get(weekNumber);
    if (cached) return cached;

    const resolved =
        resolveAsOf(lookup.configurationVersions?.workingHours, weekNumber) ??
        lookup.configuration;
    byWeek.set(weekNumber, resolved);
    return resolved;
}

export function resolveThresholds(
    lookup: OptimizedLookupData,
    weekNumber: number
): ThresholdsConfig {
    let byWeek = thresholdsCache.get(lookup);
    if (!byWeek) {
        byWeek = new Map<number, ThresholdsConfig>();
        thresholdsCache.set(lookup, byWeek);
    }
    const cached = byWeek.get(weekNumber);
    if (cached) return cached;

    const resolved =
        resolveAsOf(lookup.configurationVersions?.thresholds, weekNumber) ??
        lookup.configuration;
    byWeek.set(weekNumber, resolved);
    return resolved;
}
